import {
  FlaskConical,
  Truck,
  MessageSquareWarning,
  CheckCircle2,
  Clock,
  ArrowRight,
  ShieldCheck,
  AlertTriangle,
  Package,
  type LucideIcon,
} from 'lucide-react';
import { StatCard, type StatTone } from '@/shared/components/ui/StatCard';
import { Card } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import type { SampleRequest, SampleTrackingSummary } from '../types';

interface SampleTrackingCardProps {
  tracking?: SampleTrackingSummary;
  isLoading?: boolean;
  onSelectSample?: (sample: SampleRequest) => void;
  onQuickFeedback?: (sample: SampleRequest) => void;
}

export function SampleTrackingCard({
  tracking,
  isLoading,
  onSelectSample,
  onQuickFeedback,
}: SampleTrackingCardProps) {
  const counts = tracking?.counts || {};
  const awaitingList = tracking?.awaitingFeedback || [];

  // Every open stage is counted somewhere, so the strip always adds up to the
  // samples actually in progress (a sample waiting on Dispatch used to be in none).
  const statTiles: { label: string; count: number; icon: LucideIcon; tone: StatTone }[] = [
    { label: 'Draft', count: counts.draft || 0, icon: Clock, tone: 'neutral' },
    { label: 'With Dispatch', count: counts.to_dispatch || 0, icon: Package, tone: 'warning' },
    { label: 'In Transit', count: (counts.dispatched || 0) + (counts.delivered || 0), icon: Truck, tone: 'info' },
    { label: 'Feedback Due', count: awaitingList.length, icon: MessageSquareWarning, tone: 'warning' },
    { label: 'Tech Review', count: counts.technical_review || 0, icon: ShieldCheck, tone: 'primary' },
    { label: 'Converted', count: counts.converted || 0, icon: CheckCircle2, tone: 'success' },
  ];

  return (
    <div className="space-y-4">
      {/* Metric Tiles */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        {statTiles.map((tile) => (
          <StatCard key={tile.label} label={tile.label} value={tile.count} icon={tile.icon} tone={tile.tone} loading={isLoading} />
        ))}
      </div>

      {/* Overdue Feedback Chase List */}
      {awaitingList.length > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50/40">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-amber-100 text-amber-800">
                <AlertTriangle className="w-4 h-4" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Samples Awaiting Customer Verdict ({awaitingList.length})
                </h4>
                <p className="text-data-xs text-slate-500">
                  Dispatched samples without recorded feedback. Chase these accounts to keep the deal moving forward.
                </p>
              </div>
            </div>
            <Badge variant="warning" className="text-data-xs font-bold">
              Action Required
            </Badge>
          </div>

          <div className="divide-y divide-amber-100/80 max-h-56 overflow-y-auto pr-1">
            {awaitingList.map((sample) => {
              const daysAgo = sample.dispatch?.dispatchedAt
                ? Math.floor(
                    (new Date().getTime() - new Date(sample.dispatch.dispatchedAt).getTime()) /
                      (1000 * 60 * 60 * 24),
                  )
                : null;

              return (
                <div
                  key={sample._id}
                  className="py-2.5 flex items-center justify-between hover:bg-amber-100/30 px-2 rounded-lg transition"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-white border border-amber-200">
                      <FlaskConical className="w-3.5 h-3.5 text-amber-700" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-xs text-slate-900">
                          {sample.customerName || 'Unnamed Customer'}
                        </span>
                        <span className="text-data-xs font-mono font-medium text-slate-400">
                          {sample.code}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-data-xs text-slate-500">
                        {sample.lines && sample.lines.length > 0 && (
                          <span>
                            {sample.lines[0].productName || 'Product'} ({sample.lines[0].packSize})
                            {sample.lines.length > 1 ? ` +${sample.lines.length - 1} more` : ''}
                          </span>
                        )}
                        {sample.dispatch?.courier && (
                          <span>· Courier: {sample.dispatch.courier}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {daysAgo !== null && (
                      <span className="text-data-xs font-semibold text-amber-900 bg-amber-100/80 px-2 py-0.5 rounded">
                        {daysAgo} {daysAgo === 1 ? 'day' : 'days'} out
                      </span>
                    )}
                    {onQuickFeedback && (
                      <Button
                        size="sm"
                        variant="secondary"
                        className="text-xs h-7 bg-white hover:bg-amber-100 border-amber-200"
                        onClick={() => onQuickFeedback(sample)}
                      >
                        Record Verdict
                      </Button>
                    )}
                    {onSelectSample && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs h-7 px-2"
                        onClick={() => onSelectSample(sample)}
                      >
                        <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}
